import { reactive } from "vue";
import { apiUrl } from "@/utils/api";
import { removeAuthBackup } from "@/utils/cookieBackup";

// Centralized wallet/balance state with caching and in-flight de-dup
const TTL_MS = 15000; // 15s cache TTL

export const walletState = reactive({
  walletBalance: null,
  isLoggedIn: false,
  loading: false,
  lastFetchedAt: 0,
  inFlight: null,
});

function getToken() {
  try {
    return localStorage.getItem("access_token");
  } catch (_) {
    return null;
  }
}

export async function fetchBalance(options = {}) {
  const { force = false } = options;
  const token = getToken();

  // Determine auth primarily from token presence
  const hasToken = !!token;
  walletState.isLoggedIn = hasToken;

  // If no token, do not call the API
  if (!hasToken) {
    walletState.walletBalance = null;
    return Promise.resolve(null);
  }

  // Respect TTL cache unless forced
  const now = Date.now();
  if (
    !force &&
    walletState.lastFetchedAt &&
    now - walletState.lastFetchedAt < TTL_MS
  ) {
    return Promise.resolve(walletState.walletBalance);
  }

  // De-duplicate in-flight requests
  if (walletState.inFlight) {
    return walletState.inFlight;
  }

  walletState.loading = true;
  const controller = new AbortController();
  const signal = controller.signal;

  const p = (async () => {
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
      // include x-country-code when available (as many components already do)
      if (
        typeof window !== "undefined" &&
        window.appRegion &&
        window.appRegion.phone_code
      ) {
        headers["x-country-code"] = window.appRegion.phone_code;
      }

      const response = await fetch(
        `${apiUrl}/wallets/get-balance?secret=a463c0cd-78ed-4bda-a5f6-f70c3e597dfb`,
        { method: "GET", headers, signal }
      );

      if (response.ok) {
        const data = await response.json();
        // Support different response shapes
        let raw =
          (data &&
            (data.wallet_balance ??
              data.walletBalance ??
              data.balance ??
              data.amount ??
              data.value)) ??
          (data &&
            data.data &&
            (data.data.wallet_balance ?? data.data.balance));
        // Coerce to number if possible, otherwise keep raw
        let parsed = raw;
        if (typeof raw === "string") {
          const cleaned = raw.replace(/[^0-9.,-]/g, "").replace(/,/g, ".");
          const num = Number(cleaned);
          if (!Number.isNaN(num)) parsed = num;
        }
        walletState.walletBalance = parsed;
        walletState.isLoggedIn = true; // confirmed by server
        walletState.lastFetchedAt = Date.now();
        return walletState.walletBalance;
      } else {
        // On auth failure, reflect logged-out state and clear invalid token
        walletState.isLoggedIn = false;
        walletState.walletBalance = null;
        walletState.lastFetchedAt = Date.now();
        // Clear invalid token from localStorage
        if (response.status === 401 || response.status === 403) {
          try {
            localStorage.removeItem("access_token");
            localStorage.removeItem("expires_in");
            removeAuthBackup("access_token");
            removeAuthBackup("expires_in");
          } catch (_) {}
        }
        return null;
      }
    } catch (e) {
      // Network or other error -> keep token-derived auth if needed, but no balance
      walletState.walletBalance = null;
      // If fetch failed, we cannot confirm; prefer token-derived state already set
      return null;
    } finally {
      walletState.loading = false;
      walletState.inFlight = null;
    }
  })();

  walletState.inFlight = p;
  return p;
}
